import {Injectable, NgZone, OnDestroy} from '@angular/core';
import {SocialAuthService, SocialUser} from '@abacritt/angularx-social-login';
import {Router} from '@angular/router';
import {firstValueFrom, Observable, Subscription, timeout, filter, map, catchError, of} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User } from '../models/user';
import { environment } from '../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import {JwtHelperService} from "@auth0/angular-jwt";

/** How long to wait for Google auto-login before giving up (ms). */
const AUTH_WAIT_TIMEOUT_MS = 5000;

/** Refresh margin: schedule token refresh this many ms before actual expiry. */
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000; // 5 minutes

@Injectable({
  providedIn: 'root'
})
export class UserService implements OnDestroy {
  userObservable: Observable<SocialUser>;
  socialUser: SocialUser;
  timeout;
  timeoutPromise;
  accessToken = '';
  redirectUrl: string;
  authSubscription: Subscription = null;
  jwtHelper: JwtHelperService = new JwtHelperService();
  private expiryTimerId: ReturnType<typeof setTimeout> | null = null;
  private visibilityListener: (() => void) | null = null;

  constructor(
    private socialAuthService: SocialAuthService,
    private router: Router,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private ngZone: NgZone
  ) {
    this.userObservable = this.socialAuthService.authState;

    let storedSocialUser: SocialUser = JSON.parse(sessionStorage.getItem('socialUser'));
    if (storedSocialUser && storedSocialUser.idToken) {
      if (!this.jwtHelper.isTokenExpired(storedSocialUser.idToken)) {
        this.socialUser = storedSocialUser;
        this.scheduleTokenExpiryCheck(storedSocialUser.idToken);
      } else {
        sessionStorage.removeItem('socialUser');
      }
    }
    this.subscribeToAuthState();
  }

  ngOnDestroy(): void {
    this.clearExpiryTimer();
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  subscribeToAuthState() {
    if (!this.authSubscription) {
      this.authSubscription = this.socialAuthService.authState.subscribe((socialUser) => {
        let storedSocialUser: SocialUser = JSON.parse(sessionStorage.getItem('socialUser'));
        if (storedSocialUser && storedSocialUser.idToken) {
          if (!this.jwtHelper.isTokenExpired(storedSocialUser.idToken)) {
            this.socialUser = storedSocialUser;
            this.scheduleTokenExpiryCheck(storedSocialUser.idToken);
            return;
          } else {
            sessionStorage.removeItem('socialUser');
          }
        }

        this.socialUser = socialUser;

        if (socialUser != null) {
          sessionStorage.setItem('socialUser', JSON.stringify(socialUser));
          this.scheduleTokenExpiryCheck(socialUser.idToken);
        }
      });
    }
  }

  isLoggedIn(): boolean {
    return this._getUser() != null;
  }

  private _getUser(): (SocialUser | null) {
    if (this.socialUser && !this.jwtHelper.isTokenExpired(this.socialUser.idToken)) {
      return this.socialUser;
    }
    let storedSocialUser: SocialUser = JSON.parse(sessionStorage.getItem('socialUser'));
    if (storedSocialUser && storedSocialUser.idToken) {
      if (!this.jwtHelper.isTokenExpired(storedSocialUser.idToken)) {
        this.socialUser = storedSocialUser;
        return this.socialUser;
      }
    }
    this.socialUser = null;
    sessionStorage.removeItem('socialUser');
    return this.socialUser;
  }

  /**
   * Returns the current user if available, or waits up to AUTH_WAIT_TIMEOUT_MS
   * for Google auto-login to emit a valid user via authState. Returns null if
   * no user is available after the timeout (instead of hanging forever).
   */
  getUser(): Promise<SocialUser | null> {
    // Fast path: user already available
    const existing = this._getUser();
    if (existing) {
      return Promise.resolve(existing);
    }

    // Wait for authState to emit a valid (non-expired) user, with a timeout
    return firstValueFrom(
      this.socialAuthService.authState.pipe(
        filter(user => user != null && !this.jwtHelper.isTokenExpired(user.idToken)),
        timeout(AUTH_WAIT_TIMEOUT_MS),
        catchError(() => of(null))
      )
    );
  }

  /** Clears the session and navigates to sign-in. */
  signOutAndRedirect(): void {
    this.clearExpiryTimer();
    this.socialUser = null;
    sessionStorage.removeItem('socialUser');
    sessionStorage.removeItem('user');
    this.router.navigate(['/sign-in']);
  }

  getUserRole(): Promise<User> {
    let storedUserRole: User = JSON.parse(sessionStorage.getItem('user'));
    if (storedUserRole && this._getUser()) {
      return Promise.resolve(storedUserRole);
    }

    return this.getUser().then(user => {
      if (!user) {
        this.signOutAndRedirect();
        return null;
      }
      const headers = new HttpHeaders({'Authorization': `Bearer ${user.idToken}`});
      return firstValueFrom(this.http.get(environment.baseUrl + `/api/email-roles`, {headers: headers}))
        .catch(data => this.snackBar.open(data.message, "close", {duration: 5000}))
        .then(data => {
          let role: User = <User>data;
          if (role && role.Email && role.Role) {
            sessionStorage.setItem('user', JSON.stringify(role));
          }
          return <User>data
        })
    });
  }

  /**
   * Schedules a timer to fire shortly before the token expires.
   * When it fires, it only acts if the tab is in the foreground
   * (document.visibilityState === 'visible'), preventing idle
   * background tabs from triggering login prompts.
   */
  private scheduleTokenExpiryCheck(token: string): void {
    this.clearExpiryTimer();

    const expiryDate = this.jwtHelper.getTokenExpirationDate(token);
    if (!expiryDate) {
      return;
    }

    const msUntilExpiry = expiryDate.getTime() - Date.now();
    const delay = Math.max(msUntilExpiry - TOKEN_REFRESH_MARGIN_MS, 0);

    // Run outside Angular zone so the timer doesn't trigger change detection
    this.ngZone.runOutsideAngular(() => {
      this.expiryTimerId = setTimeout(() => {
        // Only redirect if the tab is in the foreground
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          this.ngZone.run(() => {
            this.snackBar.open('Your session has expired. Please sign in again.', 'close', { duration: 10000 });
            this.signOutAndRedirect();
          });
        } else {
          // Tab is hidden — register a one-time listener to redirect when the user returns
          this.clearVisibilityListener();
          const onVisible = () => {
            if (document.visibilityState === 'visible') {
              this.clearVisibilityListener();
              this.ngZone.run(() => {
                // Re-check: the auth state subscription may have refreshed the token
                if (!this._getUser()) {
                  this.snackBar.open('Your session has expired. Please sign in again.', 'close', { duration: 10000 });
                  this.signOutAndRedirect();
                }
              });
            }
          };
          this.visibilityListener = onVisible;
          document.addEventListener('visibilitychange', onVisible);
        }
      }, delay);
    });
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimerId !== null) {
      clearTimeout(this.expiryTimerId);
      this.expiryTimerId = null;
    }
    this.clearVisibilityListener();
  }

  private clearVisibilityListener(): void {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  }
}
